import React, { useEffect, useState, useGlobal, useMemo } from 'reactn';
import ReactDOM from 'react-dom';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
  PaginationTotalStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import cellEditFactory from 'react-bootstrap-table2-editor';
import { Button, Col, Input, InputGroup, Row } from 'reactstrap';
import { DateRangePicker } from 'react-dates';
import moment from 'moment';
import { withApollo } from 'react-apollo';
import {
  weeklyValidationQuery,
  weeklyValidationCSVQuery,
  insertGasReadingsAction,
  insertGasReadingsEditedData,
} from '../../../../queries/Queries';
import Loading from '../../../../components/Loading';
import ExpiringAlert from '../../../../components/ExpiringAlert/ExpiringAlert';
import BulkApproveConfirmModal from '../../../../views/Clients/Hartree/BulkApproveConfirmModal/BulkApproveConfirmModal';
import { columns, READ_STATUS } from './columns';
import { isDateOlderThanDays, ReportActions } from '../../../../utils/Helpers';

const CustomSelectionHeader = ({ checked, indeterminate, onAllRowsSelect }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <div className='checkbox-wrapper'>
      <input
        type='checkbox'
        className='css-checkbox'
        checked={checked}
        ref={(input) => {
          if (input) input.indeterminate = indeterminate;
        }}
        onChange={onAllRowsSelect}
      />
      <i
        className={`icon ${checked ? 'ion-android-checkbox' : 'ion-android-checkbox-outline-blank'}`}
        style={{ fontSize: '20px', cursor: 'pointer' }}
      />
    </div>
  </div>
);

const SelectionCell = ({ row, checked, disabled, onChange }) => (
  <div className='checkbox-wrapper'>
    <input type='checkbox' className='css-checkbox' checked={checked} disabled={disabled} onChange={onChange} />
    <i
      className={`icon ${checked ? 'ion-android-checkbox' : 'ion-android-checkbox-outline-blank'}`}
      style={{
        fontSize: '20px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    />
  </div>
);

const WeeklyValidation = ({ client }) => {
  const [username, setUsername] = useGlobal('username');
  // State variables for manage UI data
  const [weeklyValidationData, setWeeklyValidationData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [recalculateRows, setRecalculateRows] = useState([]);
  const [refetchTableData, setRefetchTableData] = useState(false);

  // State variables for manage UI
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const [isExporting, setIsExporting] = useState(false);

  // State variables for manage loading, modal and focus date picker
  const [loading, setLoading] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [focus, setFocus] = useState(null);
  const [sortState, setSortState] = useState({ dataField: 'date', order: 'desc' });

  // Create where clause for filtering
  const buildWhereClause = () => {
    const whereConditions = [];

    // Search term filtering (MPRN or Site) - using debounced search term
    if (debouncedSearchTerm && debouncedSearchTerm.length > 0) {
      whereConditions.push({
        _or: [{ mprn: { _like: `%${debouncedSearchTerm}%` } }, { site: { _ilike: `%${debouncedSearchTerm}%` } }],
      });
    }

    // Date range filtering
    if (dateRange.startDate && dateRange.endDate) {
      whereConditions.push({
        _and: [
          { date: { _gte: moment(dateRange.startDate).format('YYYY-MM-DD') } },
          { date: { _lte: moment(dateRange.endDate).format('YYYY-MM-DD') } },
        ],
      });
    }

    // Default status filtering - show only specific statuses using imported READ_STATUS keys
    const statusFilter = { status: { _in: ['PENDING_APPROVAL', 'RECALCULATING', 'FAILED', 'MISSING_DATA'] } };

    // If we have search term or date range, don't apply default status filter
    // unless explicitly needed for business logic
    if (whereConditions.length === 0) {
      whereConditions.push(statusFilter);
    }

    // Combine all conditions with _and
    return whereConditions.length > 1 ? { _and: whereConditions } : whereConditions[0] || {};
  };

  // Debounce search term with 300ms delay
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);

    const offset = (currentPage - 1) * currentSizePerPage;
    const whereClause = buildWhereClause();
    const orderByClause = sortState.dataField ? { [sortState.dataField]: sortState.order } : { date: 'desc' };

    client
      .query({
        query: weeklyValidationQuery,
        variables: {
          limit: currentSizePerPage,
          offset: offset,
          where: whereClause,
          orderBy: orderByClause,
          _timestamp: Date.now(),
        },
        fetchPolicy: 'network-only',
        context: {
          fetchOptions: {
            signal: abortController.signal,
          },
        },
      })
      .then((res) => {
        if (abortController.signal.aborted) return;

        if (res.data && res.data.gas_readings_latest) {
          setWeeklyValidationData(
            res.data.gas_readings_latest.map((reading) => ({
              ...reading,
              id: reading.id,
            })),
          );
        } else {
          setWeeklyValidationData([]);
        }

        if (res.data && res.data.gas_readings_latest_aggregate && res.data.gas_readings_latest_aggregate.aggregate) {
          setTotalCount(res.data.gas_readings_latest_aggregate.aggregate.count);
        } else {
          setTotalCount(0);
        }
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        // Only log non-abort errors
        if (error.name !== 'AbortError') {
          console.error('Error fetching weekly validation data:', error);
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [
    client,
    refetchTableData,
    currentPage,
    currentSizePerPage,
    debouncedSearchTerm,
    dateRange.startDate,
    dateRange.endDate,
    sortState.dataField,
    sortState.order,
  ]);

  // Reset to page 1 when search term changes or both start and end dates are defined
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    dateRange.startDate && dateRange.endDate ? dateRange.startDate : null,
    dateRange.startDate && dateRange.endDate ? dateRange.endDate : null,
  ]);

  // Prepare gas reading action for mutation
  const prepareReading = (reading, action) => {
    return reading.map((r) => {
      return {
        gas_reading_id: r,
        action: action,
        createdby: username,
      };
    });
  };

  const handleInsertGasReadingsAction = async (gasReadingActions) => {
    await client.mutate({
      mutation: insertGasReadingsAction,
      variables: { objects: gasReadingActions },
    });
    setSelectedRows([]);
    setRefetchTableData(!refetchTableData);
  };

  const handleInserRecalculatedGasReadings = async () => {
    await client.mutate({
      mutation: insertGasReadingsEditedData,
      variables: { gasReadings: recalculateRows },
    });
    setRecalculateRows([]);
    setRefetchTableData(!refetchTableData);
  };

  // Submit gas reading action
  const submitReading = async (params, action) => {
    setLoading(true);
    let alertColor = 'success';
    let alertMsg = 'Reading submitted successfully';

    try {
      const readingData = Array.isArray(params) ? params : [params];
      const reading = prepareReading(readingData, action);
      await handleInsertGasReadingsAction(reading);
    } catch (error) {
      alertColor = 'danger';
      alertMsg = error.message.includes(' : ') ? error.message.split(' : ')[1] : error.message;
    } finally {
      setLoading(false);
      ReactDOM.render(<ExpiringAlert color={alertColor} message={alertMsg} />, document.getElementById('alert'));
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleSizePerPageChange = (sizePerPage) => {
    setCurrentSizePerPage(sizePerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    if (type === 'pagination') {
      if (page !== currentPage) {
        handlePageChange(page);
      }
      if (sizePerPage !== currentSizePerPage) {
        handleSizePerPageChange(sizePerPage);
      }
    }
    if (type === 'sort') {
      setSortState({ dataField: sortField, order: sortOrder });
      setCurrentPage(1); // Reset to first page when sorting changes
    }
  };

  const getHeaders = () => {
    const headers = [];
    const tableColumnsCSV = columns(submitReading, recalculateRows, true);
    for (const col of tableColumnsCSV) {
      if (col.dataField) headers.push({ label: col.text, key: col.dataField });
    }
    return headers;
  };

  const handleFileName = () => {
    if (dateRange.endDate) {
      const startDate = new Date(dateRange.startDate).toLocaleDateString();
      const endDate = new Date(dateRange.endDate).toLocaleDateString();
      return `${startDate} ${endDate}.csv`;
    }
    return 'WeeklyValidation_Export.csv';
  };

  const tableColumns = useMemo(() => columns(submitReading, recalculateRows, false), [submitReading, recalculateRows]);

  const paginationOptions = useMemo(
    () =>
      paginationFactory({
        custom: true,
        page: currentPage,
        sizePerPage: currentSizePerPage,
        totalSize: totalCount,
        alwaysShowAllBtns: true,
        sizePerPageList: [
          { text: '5', value: 5 },
          { text: '10', value: 10 },
          { text: '20', value: 20 },
          { text: '50', value: 50 },
          { text: '75', value: 75 },
          { text: '100', value: 100 },
        ],
        onPageChange: handlePageChange,
        onSizePerPageChange: handleSizePerPageChange,
      }),
    [currentPage, currentSizePerPage, totalCount, handlePageChange, handleSizePerPageChange],
  );

  // Handle cell edit
  const handleCellEdit = (oldValue, newValue, row, column) => {
    let alertColor = 'danger';
    let alertMsg = '';

    // Check if the new value is empty
    if (newValue === null || newValue === undefined || newValue.trim() === '') {
      alertMsg = 'Cell value cannot be empty';
      ReactDOM.render(<ExpiringAlert color={alertColor} message={alertMsg} />, document.getElementById('alert'));
      row[column.dataField] = oldValue;
      return;
    }

    // Check if the new value contains only numeric characters
    if (!/^\d+(\.\d+)?$/.test(newValue.trim())) {
      alertMsg = 'Cell value must be a valid number';
      ReactDOM.render(<ExpiringAlert color={alertColor} message={alertMsg} />, document.getElementById('alert'));
      row[column.dataField] = oldValue;
      return;
    }

    // Parse the new value and update the row
    const parsedNewValue = parseFloat(newValue);
    row[column.dataField] = parsedNewValue;

    // Check if the value has changed
    if (oldValue !== parsedNewValue) {
      // Add the data to the state.
      // recalculateRows is an array of objects.
      // Check if the row id is already in the state, if it is, update the row, if not, add the row
      setRecalculateRows((prevState) => {
        // Check if the row is already in the state
        const index = prevState.findIndex((r) => r.gas_reading_id === row.id);

        if (index !== -1) {
          // Update the row with the new values and assign row.id to gas_reading_id
          prevState[index][column.dataField] = parsedNewValue;

          return prevState;
        } else {
          // Add the row to the state
          let recalculationFields = {
            corrected_cns_consumption: null,
            corrected_rd1_reading: null,
            createdby: username,
            gas_reading_id: null,
            meter_reading: null,
            total_power_exported: null,
            uncorrected_cns_consumption: null,
            uncorrected_rd1_reading: null,
          };
          recalculationFields[column.dataField] = parsedNewValue;
          return [...prevState, { ...recalculationFields, gas_reading_id: row.id }];
        }
      });
    }
  };

  const handleSelectRow = (row, isSelect) => {
    if (READ_STATUS[row.status] !== READ_STATUS.PENDING_APPROVAL) return false;

    if (isSelect) {
      setSelectedRows((prevSelected) => [...prevSelected, row.id]);
    } else {
      setSelectedRows((prevSelected) => prevSelected.filter((id) => id !== row.id));
    }
    return true; // This ensures the checkbox can be toggled
  };

  const handleSelectAll = (isSelect, rows) => {
    if (isSelect) {
      const pendingRows = rows
        .filter((row) => READ_STATUS[row.status] === READ_STATUS.PENDING_APPROVAL)
        .map((row) => row.id);
      setSelectedRows((prevSelected) => [...prevSelected, ...pendingRows]);
    } else {
      const rowsIds = rows.map((row) => row.id);
      setSelectedRows((prevSelected) => prevSelected.filter((id) => !rowsIds.includes(id)));
    }
    return true;
  };

  const handleCSVExport = async () => {
    try {
      if (totalCount === 0) {
        return;
      }

      setIsExporting(true);
      const whereClause = buildWhereClause();

      // Fetch all records in one request
      const allDataResponse = await client.query({
        query: weeklyValidationCSVQuery,
        variables: {
          limit: totalCount,
          offset: 0,
          where: whereClause,
        },
        fetchPolicy: 'no-cache',
      });

      const allData = allDataResponse.data ? allDataResponse.data.gas_readings_latest : [];

      // Create CSV content manually and trigger download
      const headers = getHeaders();
      const csvContent = [
        // Headers row
        headers.map((header) => header.label).join(','),
        // Data rows
        ...allData.map((row) =>
          headers
            .map((header) => {
              const value = row[header.key];
              // Handle values that might contain commas or quotes
              if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                return `"${value.replace(/"/g, '""')}"`;
              }
              return value || '';
            })
            .join(','),
        ),
      ].join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', handleFileName());
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      ReactDOM.render(
        <ExpiringAlert color='danger' message='Error exporting CSV data' />,
        document.getElementById('alert'),
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <Row className='subheader'>
        <Col className='col-8'>
          <h1>Weekly Validation</h1>
        </Col>
      </Row>
      <div className='dashboard-content hartree-hasura-grid'>
        <div className='table-filers-cnt'>
          <div className='table-filers-elements'>
            <Row>
              <Col xs='6' sm='6'>
                <InputGroup className='search'>
                  <Input
                    placeholder='Search by MPRN or Site'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <i className='icon ion-ios-search-strong' />
                </InputGroup>
              </Col>
              <Col xs='4' sm='4'>
                <DateRangePicker
                  small
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  onDatesChange={(date) => setDateRange(date)}
                  displayFormat={'MM/DD/YYYY'}
                  focusedInput={focus}
                  onFocusChange={(focus) => setFocus(focus)}
                  showClearDates={true}
                  showDefaultInputIcon={true}
                  isOutsideRange={() => false}
                  noBorder={false}
                />
              </Col>
            </Row>
          </div>
        </div>

        <PaginationProvider pagination={paginationOptions}>
          {({ paginationProps, paginationTableProps }) => (
            <div>
              <div className='d-flex flex-column flex-lg-row align-items-start justify-content-between'>
                <Col className='d-flex flex-column align-items-start' lg='8' md='7' sm='4' xs='12'>
                  <ul className='efficiencyMethodLegend d-flex flex-row align-items-center justify-content-between'>
                    <li className='d-flex align-items-center'>
                      <span className='cns'></span> CNS Corrected Consumption
                    </li>
                    <li className='d-flex align-items-center'>
                      <span className='rd1'></span> RD1 Corrected Consumption
                    </li>
                  </ul>
                  <div className='d-flex flex-column flex-lg-row'>
                    <Button
                      className='mr-2 mb-2'
                      color='primary'
                      disabled={selectedRows.length === 0}
                      onClick={() => setIsConfirmModalOpen(true)}
                    >
                      Bulk Approve ({selectedRows.length})
                    </Button>
                    <Button
                      className='mr-2 mb-2'
                      color='primary'
                      disabled={recalculateRows.length === 0}
                      onClick={async () => await handleInserRecalculatedGasReadings()}
                    >
                      Recalculate ({recalculateRows.length})
                    </Button>
                    <Button className='mr-2 mb-2' color='primary' disabled={isExporting} onClick={handleCSVExport}>
                      {isExporting ? 'Exporting...' : 'Download CSV'}
                    </Button>
                  </div>
                </Col>
                <Col lg='4' md='4' sm='4' xs='12' className='d-flex flex-column align-items-end'>
                  <SizePerPageDropdownStandalone {...paginationProps} />
                  <PaginationTotalStandalone {...paginationProps} />
                  <PaginationListStandalone {...paginationProps} />
                </Col>
              </div>

              <div>
                {loading && <Loading />}
                <BootstrapTable
                  headerClasses='custom-header-class'
                  wrapperClasses='table-responsive'
                  keyField='id'
                  columns={tableColumns}
                  data={weeklyValidationData}
                  defaultSorted={[
                    {
                      dataField: 'date',
                      order: 'desc',
                    },
                  ]}
                  noDataIndication={() => <div>No records in table</div>}
                  onTableChange={handleTableChange}
                  remote={{
                    filter: true,
                    pagination: true,
                    sort: true,
                    cellEdit: false,
                  }}
                  selectRow={{
                    mode: 'checkbox',
                    clickToSelect: false,
                    selected: selectedRows,
                    onSelect: handleSelectRow,
                    onSelectAll: handleSelectAll,
                    nonSelectable: weeklyValidationData
                      .filter(
                        (row) =>
                          READ_STATUS[row.status] !== READ_STATUS.PENDING_APPROVAL || !isDateOlderThanDays(row.date, 6),
                      )
                      .map((row) => row.id),
                    selectionHeaderRenderer: ({ mode, ...rest }) => <CustomSelectionHeader mode={mode} {...rest} />,
                    selectionRenderer: ({ checked, disabled, onChange, row }) => (
                      <SelectionCell row={row} checked={checked} disabled={disabled} onChange={onChange} />
                    ),
                    style: { backgroundColor: '#F7F9F9' },
                  }}
                  rowStyle={(row) =>
                    READ_STATUS[row.status] == READ_STATUS.MISSING_DATA || row.is_reading_missing
                      ? { boxShadow: 'inset 5px 0 #ff0000' }
                      : {}
                  }
                  cellEdit={cellEditFactory({
                    mode: 'dbclick',
                    blurToSave: true,
                    afterSaveCell: handleCellEdit,
                  })}
                  {...paginationTableProps}
                />
              </div>
            </div>
          )}
        </PaginationProvider>

        {/* Modals */}
        <BulkApproveConfirmModal
          isOpen={isConfirmModalOpen}
          toggle={() => setIsConfirmModalOpen(false)}
          onConfirm={() => {
            setIsConfirmModalOpen(false);
            submitReading(selectedRows, ReportActions.Approve);
          }}
          selectedCount={selectedRows.length}
        />
      </div>
    </div>
  );
};

export default withApollo(WeeklyValidation);
